import { useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Automation2Service from 'services/Automation2Service';
import { AUTOMATIONS } from 'queries/query-keys';
import {
  LLMPluginApp,
  LLMProviderModel,
  ModelOption,
  ProviderOption,
} from './types';
import { optionsFlatMapper } from './helpers';

const addRecommended = (recommended: boolean, t: TFunction) =>
  recommended ? ` ${t('(Recommended)')}` : '';

type UseProviderOptionsProps = {
  modelName?: string;
  isForImage?: boolean;
  isForDecision?: boolean;
  isForCall?: boolean;
  isForAudioTranslation?: boolean;
};

type AutomtionMetadataLike = {
  llm: {
    provider_model_details: {
      plugin_app: LLMPluginApp;
      models: LLMProviderModel[];
    }[];
  };
};

export const useProviderOptions = ({
  modelName,
  isForImage = false,
  isForDecision = false,
  isForCall = false,
  isForAudioTranslation = false,
}: UseProviderOptionsProps) => {
  const { t } = useTranslation();
  const {
    data: { providerOptions, flatAllProviders, flatProviderOptions } = {
      allProviders: [],
      flatAllProviders: undefined,
      providerOptions: [],
      flatProviderOptions: [],
    },
    isLoading: isProvidersLoading,
  } = useQuery({
    queryKey: AUTOMATIONS.META_DATA,
    queryFn: () => {
      return Automation2Service.getMetaData();
    },
    refetchOnWindowFocus: 'always', // so that if the user updates the market place focus updates always
    select: ({ llm: { provider_model_details } }: AutomtionMetadataLike) => {
      const allProviders = provider_model_details.reduce(
        (previous, current) => {
          const { plugin_app, models } = current;
          const next = {
            label: plugin_app.plugin_app_name,
            details: plugin_app,
            options: models.reduce<ModelOption[]>((acc, model) => {
              if (
                (!isForImage &&
                  !isForDecision &&
                  !isForCall &&
                  !isForAudioTranslation) ||
                (isForImage && model.usage.image.extraction) ||
                (isForDecision && model.usage.text.decision) ||
                (isForCall && model.usage.text.call) ||
                (isForAudioTranslation && model.usage.audio.transcription)
              ) {
                const labelWithRecommended = `${model.model_label}${addRecommended((isForImage && model.usage.image.recommended) || (isForAudioTranslation && model.usage.audio.recommended), t)}`;
                const label = model.is_deprecated
                  ? t('{{label}} (Deprecated)', { label: labelWithRecommended })
                  : labelWithRecommended;
                acc.push({
                  value: model.model_value,
                  label,
                  isDisabled: model.is_deprecated,
                  disabled: plugin_app.disabled,
                  model: { ...model, business_plugin_app_id: plugin_app.id },
                  plugin_app,
                });
              }
              return acc;
            }, []),
          };

          if (next.options.length) {
            previous.push(next);
          }
          return previous;
        },
        [] as ProviderOption[]
      );

      const flatAllProviders = optionsFlatMapper(allProviders);

      const providerOptions = allProviders.filter(
        ({ details }) => !details.disabled
      );

      const flatProviderOptions = optionsFlatMapper(providerOptions);

      return {
        allProviders,
        flatAllProviders,
        providerOptions,
        flatProviderOptions,
      };
    },
    throwOnError: false,
  });

  const { augmentedProviderOptions, model } = useMemo(() => {
    let augmentedProviderOptions = providerOptions;
    let modelOption: ModelOption | undefined;

    if (modelName && flatAllProviders) {
      modelOption = flatProviderOptions.find(
        ({ value }) => value === modelName
      );
      if (!modelOption) {
        modelOption = flatAllProviders.find(({ value }) => value === modelName);

        if (modelOption) {
          const { plugin_app, model } = modelOption;

          const label = model.is_deprecated
            ? t('{{label}} (Deprecated)', { label: model.model_label })
            : t('{{label}} [Disabled]', {
                label: model.model_label,
              });

          const next = {
            label: plugin_app.plugin_app_name,
            details: plugin_app,
            options: [
              {
                value: model.model_value,
                label,
                isDisabled: model.is_deprecated,
                disabled: plugin_app.disabled,
                model: { ...model, business_plugin_app_id: plugin_app.id },
                plugin_app,
              },
            ],
          };

          augmentedProviderOptions = [next, ...providerOptions];
        } else {
          modelOption = {
            value: modelName,
            label: t('Unknown ({{modelName}})', { modelName }),
            disabled: true,
            model: {
              model_label: '',
              model_value: '',
              max_input_tokens: 0,
              max_output_tokens: 0,
              avg_char_per_token: 0,
              decision_token_count_base: 0,
              business_plugin_app_id: '',
              is_deprecated: false,
              usage: {
                image: {
                  extraction: false,
                  recommended: false,
                },
                audio: {
                  transcription: false,
                  recommended: false,
                },
                text: {
                  call: false,
                  decision: false,
                },
              },
            },
            plugin_app: {
              id: '',
              plugin_app_api_name: '',
              plugin_app_name: '',
              disabled: true,
              secrets: [
                {
                  api_name: '',
                  obfuscated_value: '',
                },
              ],
            },
          };

          const next = {
            label: t('Unknown'),
            details: {},
            options: [modelOption],
          };

          augmentedProviderOptions = [next, ...providerOptions];
        }
      }
    }

    return {
      augmentedProviderOptions,
      model: modelOption,
    };
  }, [providerOptions, flatProviderOptions, flatAllProviders, modelName, t]);

  return {
    isProvidersLoading,
    providerOptions: augmentedProviderOptions,
    model,
  };
};
